'use strict'

/* -----------------------------------------------------------------------------
 * dependencies
 * -------------------------------------------------------------------------- */

// 3rd party
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react'
import moment from 'moment'
import { PortalHost } from '@gorhom/portal'

// walrus
import { accessibilityLabel } from '@components/kit/_support'
import Box from '@components/kit/box'
import Text from '@components/kit/text'
import { ButtonLink } from '@components/kit/link'
import { ProfileFilledIcon, ChevronDownIcon } from '@components/kit/icons'

// lib
import { ChatContext } from '../../chat.web'

import Card from '@components/kit/card'
import Loading from '@components/kit/loading'
import { titleCase } from '@utils/string-formatters'
import Button from '@components/kit/button'
import Modal from '@components/kit/modal'
import AddMedications from './add-medications'
import StopMedication from './stop-medications'
import { IMedication } from '@store/Meds/types'
import { useMedsStore } from '@store/Meds'
import { FormButtonPanel, FormDivider } from '@components/formFields'

/* -----------------------------------------------------------------------------
 * Medications
 * -------------------------------------------------------------------------- */

enum ListType {
  Current = 'Current',
  Past = 'Past'
}

interface MedicationSummaryItem {
  drug_name: string
  drug_strength: string
}

interface GroupedMedication {
  primary: IMedication
  previousSupplies: IMedication[]
}

interface MedicationListCardProps {
  medications: IMedication[]
  isError: boolean
  error: unknown
  handleOpenModal?: (medication: any) => void
  cardListType?: ListType
}

function deduplicateAndGroup(medications: IMedication[]): GroupedMedication[] {
  const meds = medications as IMedication[]

  // Remove exact duplicates (same name + dosage + date_of_service)
  const dedupMap = new Map<string, IMedication>()
  for (const med of meds) {
    const key = `${med.drug_name}|${med.drug_strength}|${med.date_of_service}`
    if (!dedupMap.has(key)) {
      dedupMap.set(key, med)
    }
  }

  // Group by name + dosage
  const groupMap = new Map<string, IMedication[]>()
  for (const med of dedupMap.values()) {
    const key = `${med.drug_name}|${med.drug_strength}`
    if (!groupMap.has(key)) {
      groupMap.set(key, [])
    }
    groupMap.get(key)!.push(med)
  }

  // Sort each group by date desc, primary = most recent
  return Array.from(groupMap.values()).map(group => {
    const sorted = [...group].sort(
      (a, b) =>
        moment.utc(b.date_of_service).valueOf() -
        moment.utc(a.date_of_service).valueOf()
    )
    return {
      primary: sorted[0],
      previousSupplies: sorted.slice(1)
    }
  })
}

function GroupedMedicationCard({
  group,
  handleOpenModal,
  cardListType = ListType.Current
}: {
  group: GroupedMedication
  handleOpenModal?: (medication: any) => void
  cardListType?: ListType
}) {
  const [expanded, setExpanded] = useState(false)
  const { primary, previousSupplies } = group
  const isManuallyAdded = primary.drug_type === 'MANUALLY_ADDED'

  return (
    <Card
      width='100%'
      mb='20'
      flexDirection='column'
      display='flex'
      justifyContent='space-between'
    >
      <Box
        flexDirection='row'
        justifyContent='space-between'
        alignItems='center'
        width='100%'
        display='flex'
        mb={isManuallyAdded ? '00' : '12'}
      >
        <Text font='display.14.bold'>
          {primary.drug_name} - {primary.drug_strength}
        </Text>
        <Box flexDirection='row' gap='08' alignItems='center'>
          {isManuallyAdded && (
            <ProfileFilledIcon fill='violet' width={20} height={20} />
          )}
          <Button
            variant='tertiary'
            size='fit'
            flexWrap='wrap'
            minWidth='min-content'
            maxWidth='fit-content'
            onPress={() => {
              handleOpenModal?.(primary)
            }}
          >
            {cardListType == ListType.Current
              ? `Mark as Stopped`
              : `Mark as Current`}
          </Button>
        </Box>
      </Box>
      {!isManuallyAdded && (
        <Box
          flexDirection='row'
          justifyContent='space-between'
          width='100%'
          display='flex'
        >
          <Box
            justifyContent='space-between'
            style={{ flexShrink: 1, maxWidth: 'fit-content' }}
            display='flex'
          >
            <Text font='text.12.medium' textColor='blueBayoux'>
              {primary.days_supply} day supply - {primary.quantity_dispensed}{' '}
              units{' '}
            </Text>
            <Text font='text.12.medium' textColor='blueBayoux'>
              Dr. {titleCase(primary.prescribing_physician_last_name)} -{' '}
              {primary.prescribing_physician_npi}
            </Text>
          </Box>

          <Box
            justifyContent='space-between'
            alignItems='flex-end'
            style={{ flexShrink: 1, maxWidth: 'fit-content' }}
            display='flex'
          >
            <Text font='text.12.medium' textColor='blueBayoux'>
              Last filled{' '}
              {moment.utc(primary.date_of_service).format('MM/DD/YYYY')}
            </Text>
            <Text font='text.12.medium' textColor='blueBayoux'>
              $ {primary.patient_pay_amount}
            </Text>
          </Box>
        </Box>
      )}
      {previousSupplies.length > 0 && (
        <Box mt='08'>
          <Box
            flexDirection='row'
            alignItems='center'
            gap='04'
            style={{ cursor: 'pointer' } as any}
            {...{ onClick: () => setExpanded(!expanded) }}
          >
            <Text font='text.12.medium' textColor='violet'>
              Previous supplies
            </Text>
            <Box
              style={{
                transform: [{ rotate: expanded ? '180deg' : '0deg' }]
              }}
            >
              <ChevronDownIcon fill='shade06' />
            </Box>
          </Box>
          {expanded &&
            previousSupplies.map(med => (
              <Box
                key={med.transaction_id}
                mt='08'
                pt='08'
                style={{ borderTopWidth: 1, borderTopColor: '#eee' }}
              >
                {med.drug_type !== 'MANUALLY_ADDED' ? (
                  <Box
                    flexDirection='row'
                    justifyContent='space-between'
                    width='100%'
                    display='flex'
                  >
                    <Box
                      justifyContent='space-between'
                      style={{ flexShrink: 1, maxWidth: 'fit-content' }}
                      display='flex'
                    >
                      <Text font='text.12.medium' textColor='blueBayoux'>
                        {med.days_supply} day supply - {med.quantity_dispensed}{' '}
                        units{' '}
                      </Text>
                      <Text font='text.12.medium' textColor='blueBayoux'>
                        Dr. {titleCase(med.prescribing_physician_last_name)} -{' '}
                        {med.prescribing_physician_npi}
                      </Text>
                    </Box>
                    <Box
                      justifyContent='space-between'
                      alignItems='flex-end'
                      style={{ flexShrink: 1, maxWidth: 'fit-content' }}
                      display='flex'
                    >
                      <Text font='text.12.medium' textColor='blueBayoux'>
                        Last filled{' '}
                        {moment.utc(med.date_of_service).format('MM/DD/YYYY')}
                      </Text>
                      <Text font='text.12.medium' textColor='blueBayoux'>
                        $ {med.patient_pay_amount}
                      </Text>
                    </Box>
                  </Box>
                ) : (
                  <Text font='text.12.medium' textColor='blueBayoux'>
                    Manually added
                  </Text>
                )}
              </Box>
            ))}
        </Box>
      )}
    </Card>
  )
}

export function MedicationListCard({
  medications,
  isError,
  error,
  handleOpenModal,
  cardListType = ListType.Current
}: MedicationListCardProps) {
  if (isError) {
    return (
      <Card
        width='100%'
        mb='20'
        flexDirection='column'
        display='flex'
        justifyContent='space-between'
      >
        <Box pb='20'>
          <Text variant='small'>
            Failed to retrieve member medication data.
          </Text>
        </Box>
      </Card>
    )
  }

  const grouped = deduplicateAndGroup(medications)

  return (
    <>
      {grouped.map(group => (
        <GroupedMedicationCard
          key={group.primary.transaction_id}
          group={group}
          handleOpenModal={handleOpenModal}
          cardListType={cardListType}
        />
      ))}
    </>
  )
}

function MedicationSummaryCard({
  medications
}: {
  medications: MedicationSummaryItem[]
}) {
  if (!medications.length) return null

  function displayText(medications: MedicationSummaryItem[]) {
    if (medications.length === 1) {
      return `${medications[0].drug_name} ${medications[0].drug_strength}`
    } else {
      const allButLast = medications
        .slice(0, -1)
        .map(m => `${m.drug_name} ${m.drug_strength}`)
        .join(', ')
      const last = medications[medications.length - 1]
      return `${allButLast}, and ${last.drug_name} ${last.drug_strength}`
    }
  }

  return (
    <Card width='100%' marginY='20' flexDirection='column' display='flex'>
      <Text font='display.14.bold' mb='08'>
        Current Medications Summary
      </Text>
      <Text font='text.12.medium' textColor='blueBayoux'>
        {displayText(medications)}
      </Text>
    </Card>
  )
}

function MedicationList() {
  const { userId } = useContext(ChatContext)
  const [isAddMedsModalOpen, setIsAddMedsModalOpen] = useState(false)
  const [isStopMedsModalOpen, setIsStopMedsModalOpen] = useState(false)
  const [stoppingMedication, setStoppingMedication] = useState(null)
  const [hasAddClosed, setHasAddClosed] = useState(false)
  const [hasStopClosed, setHasStopClosed] = useState(false)

  const [isMedAsCurrentModalOpen, setIsMedAsCurrentModalOpen] = useState(false)
  const [markingAsCurrentMedication, setMarkingAsCurrentMedication] =
    useState<IMedication>(null)

  const [medicationSummary, setMedicationSummary] = useState<
    MedicationSummaryItem[]
  >([])

  const [isCurrentExpanded, setIsCurrentExpanded] = useState(true)
  const [isPastExpanded, setIsPastExpanded] = useState(false)
  const pastDividerRef = useRef<any>(null)

  useEffect(() => {
    const styleId = 'med-scroll-hide-scrollbar'
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style')
      style.id = styleId
      style.textContent = `
        [data-med-scroll]::-webkit-scrollbar { display: none; }
      `
      document.head.appendChild(style)
    }
  }, [])

  const { markAsCurrentMedication } = useMedsStore.getState()

  const handleOpenStopModal = medication => {
    setStoppingMedication(medication)
    setIsStopMedsModalOpen(true)
  }

  const handleOpenMarkAsCurrentModal = (medication: IMedication) => {
    setMarkingAsCurrentMedication(medication)
    setIsMedAsCurrentModalOpen(true)
  }

  const handleCloseMarkAsCurrentModal = () => {
    setIsMedAsCurrentModalOpen(false)
    setMarkingAsCurrentMedication(null)
  }

  const handleCloseAddModal = () => {
    setIsAddMedsModalOpen(false)
    setHasAddClosed(true)
  }

  const handleCloseStopModal = () => {
    setIsStopMedsModalOpen(false)
    setStoppingMedication(null)
    setHasStopClosed(true)
  }

  const handleConfirmMarkAsCurrent = async () => {
    if (markingAsCurrentMedication) {
      const success = await markAsCurrentMedication(
        userId,
        markingAsCurrentMedication.auto_id
      )
      if (success) {
        handleCloseMarkAsCurrentModal()
        refetchCurrentMed()
        refetchAllMed()
      }
    }
  }

  const { getCurrentMedications, getMedicationsPage } = useMedsStore.getState()
  const [currentMedData, setCurrentMedData] = useState<{
    lastUpdated: string
    results: IMedication[]
  } | null>(null)
  const [isLoadingCurrentMed, setIsLoadingCurrentMed] = useState(true)
  const [isErrorCurrentMed, setIsErrorCurrentMed] = useState(false)
  const [allMedData, setAllMedData] = useState<IMedication[]>([])
  const [isLoadingAllMed, setIsLoadingAllMed] = useState(true)

  const refetchCurrentMed = useCallback(async () => {
    setIsLoadingCurrentMed(true)
    const response = await getCurrentMedications(userId)
    setCurrentMedData(response)
    setIsErrorCurrentMed(!response)
    setIsLoadingCurrentMed(false)
  }, [getCurrentMedications, userId])

  // the screen only ever read the first page of the history
  const refetchAllMed = useCallback(async () => {
    setIsLoadingAllMed(true)
    const response = await getMedicationsPage(userId, 0)
    setAllMedData(response?.results ?? [])
    setIsLoadingAllMed(false)
  }, [getMedicationsPage, userId])

  useEffect(() => {
    refetchCurrentMed()
    refetchAllMed()
  }, [refetchCurrentMed, refetchAllMed])

  const pastMedData = allMedData.filter(med => {
    const isPastMed =
      !med.current &&
      !med.end_date_of_service &&
      !currentMedData?.results?.find(cm => cm.auto_id === med.auto_id)
    if (isPastMed) {
      console.log('Past med:', med)
    }
    return isPastMed
  })

  useEffect(() => {
    console.log('userId in meds for summary')
    if (currentMedData?.results) {
      const seen = new Set<string>()
      const deduped = currentMedData.results.reduce<MedicationSummaryItem[]>(
        (acc, med) => {
          const key = `${med.drug_name}|${med.drug_strength}`
          if (!seen.has(key)) {
            seen.add(key)
            acc.push({
              drug_name: titleCase(med.drug_name),
              drug_strength: (med.drug_strength || '').toLowerCase()
            })
          }
          return acc
        },
        []
      )
      setMedicationSummary(deduped)
    }
  }, [currentMedData])

  useEffect(() => {
    if (isPastExpanded && pastDividerRef.current) {
      const el: HTMLElement = pastDividerRef.current
      let parent = el.parentElement
      while (parent) {
        const { overflowY } = window.getComputedStyle(parent)
        if (overflowY === 'auto' || overflowY === 'scroll') {
          parent.scrollBy({
            top: window.innerHeight * 0.05,
            behavior: 'smooth'
          })
          break
        }
        parent = parent.parentElement
      }
    }
  }, [isPastExpanded])

  useEffect(() => {
    if (isAddMedsModalOpen) {
      setHasAddClosed(false)
    }
  }, [isAddMedsModalOpen])

  useEffect(() => {
    if (isStopMedsModalOpen) {
      setHasStopClosed(false)
    }
  }, [isStopMedsModalOpen])

  return (
    <React.Fragment>
      <Box pt='24' {...accessibilityLabel('Medications')}>
        <Box
          ml='12'
          width='100%'
          flexDirection='row'
          justifyContent='space-between'
          alignItems='center'
        >
          <Text font='display.16.bold'>
            Medications{' '}
            <Text variant='small'>(displaying the last 180 days)</Text>
            <br />
            {!isLoadingCurrentMed && !isErrorCurrentMed ? (
              <Text variant='small' textAlign='left'>
                Last claim updated at{' '}
                {moment.utc(currentMedData.lastUpdated).format('MM/DD/YYYY')}
              </Text>
            ) : null}
          </Text>
        </Box>
        <Box width='100%' mt='08' flexDirection='row' gap='04'>
          <ButtonLink
            variant='tertiary'
            size='fit'
            path={`/medication-history/${userId}`}
          >
            All med history
          </ButtonLink>
          <Button
            variant='tertiary'
            size='fit'
            onPress={() => setIsAddMedsModalOpen(true)}
          >
            Add medication
          </Button>
        </Box>
        <MedicationSummaryCard medications={medicationSummary} />
        {currentMedData?.results?.length > 0 && (
          <Box
            flexDirection='row'
            alignItems='center'
            backgroundColor='shade01b'
            {...{ onClick: () => setIsCurrentExpanded(!isCurrentExpanded) }}
            style={
              {
                cursor: 'pointer',
                position: 'sticky',
                top: 0,
                zIndex: 1
              } as any
            }
          >
            <Box flex='1 1 auto'>
              <FormDivider textColor={isCurrentExpanded ? 'shade06' : 'violet'}>
                Current Medications
                <Box
                  marginLeft='08'
                  style={{
                    transform: [
                      { rotate: isCurrentExpanded ? '180deg' : '0deg' }
                    ]
                  }}
                >
                  <ChevronDownIcon
                    fill={isCurrentExpanded ? 'shade06' : 'violet'}
                  />
                </Box>
              </FormDivider>
            </Box>
          </Box>
        )}
        {isCurrentExpanded && (
          <Box
            width='100%'
            flexDirection='column'
            justifyContent='space-between'
            alignItems='center'
            mt='08'
            {...{ 'data-med-scroll': true }}
            style={
              {
                maxHeight: '80vh',
                overflowY: 'auto',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none'
              } as any
            }
          >
            {isLoadingCurrentMed ? (
              <Card
                width='100%'
                flexDirection='column'
                display='flex'
                justifyContent='space-between'
              >
                <Loading mt='24' mb='24' />
              </Card>
            ) : (
              <MedicationListCard
                cardListType={ListType.Current}
                medications={currentMedData.results}
                isError={isErrorCurrentMed}
                error={null}
                handleOpenModal={handleOpenStopModal}
              />
            )}
          </Box>
        )}
        {pastMedData?.length > 0 && (
          <Box
            ref={pastDividerRef}
            flexDirection='row'
            alignItems='center'
            backgroundColor='shade01b'
            {...{ onClick: () => setIsPastExpanded(!isPastExpanded) }}
            style={
              {
                cursor: 'pointer',
                position: 'sticky',
                top: 0,
                zIndex: 1
              } as any
            }
          >
            <Box flex='1 1 auto'>
              <FormDivider textColor={isPastExpanded ? 'shade06' : 'violet'}>
                Past Medications
                <Box
                  marginLeft='08'
                  style={{
                    transform: [{ rotate: isPastExpanded ? '180deg' : '0deg' }]
                  }}
                >
                  <ChevronDownIcon
                    fill={isPastExpanded ? 'shade06' : 'violet'}
                  />
                </Box>
              </FormDivider>
            </Box>
          </Box>
        )}
        {isPastExpanded && (
          <Box
            width='100%'
            flexDirection='column'
            justifyContent='space-between'
            alignItems='center'
            mt='08'
            {...{ 'data-med-scroll': true }}
            style={
              {
                maxHeight: '80vh',
                overflowY: 'auto',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none'
              } as any
            }
          >
            {isLoadingAllMed ? (
              <Card
                width='100%'
                flexDirection='column'
                display='flex'
                justifyContent='space-between'
              >
                <Loading mt='24' mb='24' />
              </Card>
            ) : (
              <MedicationListCard
                cardListType={ListType.Past}
                medications={pastMedData}
                isError={false}
                error={null}
                handleOpenModal={handleOpenMarkAsCurrentModal}
              />
            )}
          </Box>
        )}
      </Box>
      <Modal
        open={isAddMedsModalOpen}
        onRequestClose={handleCloseAddModal}
        showCloseButton={true}
        width={411}
        maxHeight='90vh'
        borderRadius='medium'
        zIndex={0}
      >
        <AddMedications
          onClose={handleCloseAddModal}
          hasClosed={hasAddClosed}
          onSave={refetchCurrentMed}
        />

        <PortalHost name='addMedOverlayHost' />
        <PortalHost name='addMedHost' />
      </Modal>

      <Modal
        open={isStopMedsModalOpen}
        onRequestClose={handleCloseStopModal}
        showCloseButton={true}
        width={411}
        maxHeight='90vh'
        borderRadius='medium'
        zIndex={0}
      >
        <StopMedication
          medication={stoppingMedication}
          onCancel={handleCloseStopModal}
          onConfirm={refetchCurrentMed}
          hasClosed={hasStopClosed}
        />

        <PortalHost name='stopMedOverlayHost' />
        <PortalHost name='stopMedHost' />
      </Modal>
      <Modal
        open={isMedAsCurrentModalOpen}
        onRequestClose={handleCloseMarkAsCurrentModal}
        showCloseButton={true}
        width={411}
        maxHeight='90vh'
        borderRadius='medium'
        zIndex={0}
      >
        <Box>
          <Text font='display.16.bold' paddingBottom='16'>
            Mark as Current Medication
          </Text>
          <Text variant='small'>
            Are you sure you want to mark{' '}
            <Text font='text.14.medium'>
              {markingAsCurrentMedication?.drug_name}
            </Text>{' '}
            as a current medication?
          </Text>
          <FormButtonPanel
            onCancel={handleCloseMarkAsCurrentModal}
            onSave={handleConfirmMarkAsCurrent}
            saveLabel='Confirm'
            cancelLabel='Cancel'
          />
        </Box>
      </Modal>
    </React.Fragment>
  )
}

export default MedicationList
